import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { StickyNote, Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';

interface LocationNotesProps {
  locationId: string;
}

export function LocationNotes({ locationId }: LocationNotesProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [editText, setEditText] = useState('');

  const { data: notes = [], isLoading, isError } = useQuery({
    queryKey: ['charging-location-notes', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_location_notes')
        .select(`
          *,
          profiles:created_by(display_name, email)
        `)
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    retry: 1,
  });

  const addMutation = useMutation({
    mutationFn: async (note: string) => {
      const { error } = await supabase
        .from('charging_location_notes')
        .insert({
          note,
          location_id: locationId,
          created_by: user?.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-location-notes'] });
      toast({ title: 'Notitie toegevoegd' });
      setIsAdding(false);
      setNoteText('');
    },
    onError: (error: any) => {
      console.error('Fout bij toevoegen notitie:', error);
      toast({ 
        title: 'Fout bij opslaan notitie', 
        description: error.message || 'Probeer het opnieuw',
        variant: 'destructive' 
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      const { error } = await supabase
        .from('charging_location_notes')
        .update({ note })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-location-notes'] });
      toast({ title: 'Notitie bijgewerkt' });
      setEditingId(null);
    },
    onError: (error: any) => {
      console.error('Fout bij bijwerken notitie:', error);
      toast({ 
        title: 'Fout bij bijwerken notitie', 
        description: error.message || 'Probeer het opnieuw',
        variant: 'destructive' 
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('charging_location_notes')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-location-notes'] });
      toast({ title: 'Notitie verwijderd' });
    },
    onError: (error: any) => {
      console.error('Fout bij verwijderen notitie:', error);
      toast({ 
        title: 'Fout bij verwijderen notitie', 
        description: error.message || 'Probeer het opnieuw',
        variant: 'destructive' 
      });
    },
  });

  const handleAdd = () => {
    if (!noteText.trim()) {
      toast({ title: 'Notitie is leeg', variant: 'destructive' });
      return;
    }
    addMutation.mutate(noteText);
  };

  const handleEdit = (note: any) => {
    setEditingId(note.id);
    setEditText(note.note);
  };

  const handleUpdate = () => {
    if (!editText.trim()) {
      toast({ title: 'Notitie is leeg', variant: 'destructive' });
      return;
    }
    updateMutation.mutate({ id: editingId!, note: editText });
  };

  const handleDelete = (id: string) => {
    if (confirm('Weet je zeker dat je deze notitie wilt verwijderen?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="w-5 h-5" />
            Notities
          </CardTitle>
          <Button size="sm" onClick={() => setIsAdding(!isAdding)}>
            <Plus className="w-4 h-4 mr-1" />
            Notitie
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isAdding && (
          <div className="mb-4 space-y-2 p-4 border rounded-lg">
            <Textarea
              placeholder="Notitie tekst..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={addMutation.isPending}>
                Opslaan
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsAdding(false)}>
                Annuleren
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-4">Laden...</p>
          ) : isError ? (
            <p className="text-center text-destructive py-4">Fout bij laden van notities</p>
          ) : notes.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Geen notities toegevoegd</p>
          ) : (
            notes.map((note: any) => (
              <div key={note.id} className="p-3 bg-muted rounded-lg">
                {editingId === note.id ? (
                  <div className="space-y-2">
                    <Textarea 
                      value={editText} 
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleUpdate} disabled={updateMutation.isPending}>
                        Opslaan
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        Annuleren
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm mb-2 whitespace-pre-wrap">{note.note}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{format(new Date(note.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}</span>
                        {note.profiles && (
                          <>
                            <span>•</span>
                            <span>{note.profiles.display_name || note.profiles.email}</span>
                          </>
                        )}
                      </div>
                      {(note.created_by === user?.id) && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleEdit(note)}
                            className="h-6 w-6 p-0"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDelete(note.id)}
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
